import { useState } from "react";
import { useDashboard } from "../../context/DashboardContext";
import Sidebar from "./navigation/sideBar";
import AccountHome from "./views/AccountHome";
import DomainDetail from "./views/DomainDetail";
import EmptyState from "./EmptyState";
import defaultDashboardData from "./shared/defaultDashboardData";

// ── Topbar ─────────────────────────────────────────────────────────────────────
function Topbar({ view, activeDomain, clientData }) {
    const label = view === "domain"
        ? activeDomain
        : clientData?.firstName
            ? `${clientData.firstName} ${clientData.lastName}'s Dashboard`
            : "Dashboard";

    return (
        <header className="h-[52px] bg-white border-b border-gray-200 flex items-center px-4 gap-3 flex-shrink-0">
            <div className="flex items-center gap-2">
                <div className="w-7 h-7 bg-orange-500 rounded flex items-center justify-center">
                    <svg width="15" height="15" viewBox="0 0 100 100" fill="white">
                        <path d="M78 67c0 0-1-5-9-7l-37-7c-1 0-1-1 0-1l37-7c8-2 9-7 9-7s1-7-7-10L29 12c-6-2-13 0-16 6L5 45c-3 6-1 13 5 16l18 8v5c0 7 5 12 12 12h38c8 0 12-7 9-13z"/>
                    </svg>
                </div>
                <span className="font-semibold text-gray-800 text-sm truncate max-w-[220px]">{label}</span>
                {view === "domain" && (
                    <svg width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" className="text-gray-400">
                        <path d="M6 9l6 6 6-6"/>
                    </svg>
                )}
            </div>
            <div className="flex-1"/>
            <button className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-800">
                <svg width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83"/></svg>
                Ask AI
            </button>
            <button className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-800">
                <svg width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3M12 17h.01"/></svg>
                Support
            </button>
            <div className="w-7 h-7 rounded-full bg-orange-100 flex items-center justify-center text-orange-600 font-bold text-xs">
                {clientData?.firstName?.[0] ?? "?"}
            </div>
        </header>
    );
}

// ── App root ───────────────────────────────────────────────────────────────────
export default function DashboardHomeComponent() {
    const { clientData: apiData } = useDashboard() || {};

    // Use real API data when domains are ready; fall back to default showcase data.
    // EmptyState is shown only when the user has explicitly started onboarding
    // (apiData exists) but the SEO audit hasn't populated domains_detail yet.
    const hasRealDomains = Array.isArray(apiData?.domains_detail) && apiData.domains_detail.length > 0;
    const isNewAccount   = apiData && !hasRealDomains;
    const clientData     = hasRealDomains ? apiData : defaultDashboardData;

    const [view, setView] = useState("account");
    const [activeDomain, setActiveDomain] = useState(clientData.domains_detail[0]?.name ?? null);

    if (isNewAccount) {
        return <div className="flex flex-col h-screen overflow-hidden">
            <EmptyState clientData={apiData}/>
        </div>;
    }

    return (
        <div className="flex flex-col h-screen overflow-hidden" style={{ fontFamily: "system-ui, sans-serif" }}>
            <Topbar view={view} activeDomain={activeDomain} clientData={clientData}/>
            <div className="flex flex-1 overflow-hidden">
                <Sidebar
                    view={view} setView={setView}
                    activeDomain={activeDomain} setActiveDomain={setActiveDomain}
                />
                {view === "account" && (
                    <AccountHome
                        clientData={clientData}
                        setView={setView}
                        setActiveDomain={setActiveDomain}
                    />
                )}
                {view === "domain" && (
                    <DomainDetail
                        domainName={activeDomain}
                        clientData={clientData}
                    />
                )}
            </div>
        </div>
    );
}
